import { useState } from 'react';
import { Link } from 'react-router-dom';
import { hotComics, latestComics, rankingComics, categories } from '@/mocks/comics';
import type { Comic } from '@/mocks/comics';
import ComicCard from '@/components/feature/ComicCard';

function HeroBanner() {
  const [activeIndex, setActiveIndex] = useState(0);
  const featuredComics = hotComics.slice(0, 4);

  return (
    <section className="relative w-full h-[520px] md:h-[600px] overflow-hidden">
      {featuredComics.map((comic, idx) => (
        <div
          key={comic.id}
          className={`absolute inset-0 transition-opacity duration-700 ${idx === activeIndex ? 'opacity-100 z-10' : 'opacity-0 z-0'}`}
        >
          <img
            src={comic.cover}
            alt={comic.title}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/70" />
        </div>
      ))}

      <div className="absolute inset-0 z-20 flex items-center">
        <div className="max-w-7xl mx-auto w-full px-4 md:px-6">
          <div className="max-w-lg">
            <span className="inline-block px-3 py-1 text-xs font-medium bg-primary-500 text-white rounded-full mb-4">
              热门推荐
            </span>
            <h1 className="text-3xl md:text-5xl font-bold text-white mb-4 leading-tight">
              {featuredComics[activeIndex]?.title || '好看的韩国漫画'}
            </h1>
            <p className="text-sm md:text-base text-white/80 mb-6 line-clamp-2 leading-relaxed">
              {featuredComics[activeIndex]?.description || '哈哈漫画网为您精选优质韩国漫画，海量资源免费阅读'}
            </p>
            <div className="flex items-center gap-4">
              <Link
                to={`/article/${featuredComics[activeIndex]?.id}`}
                className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-white font-medium rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                立即阅读
                <i className="ri-arrow-right-line"></i>
              </Link>
              <span className="text-white/60 text-sm">
                {featuredComics[activeIndex]?.author} · 评分 {featuredComics[activeIndex]?.rating}
              </span>
            </div>
          </div>

          <div className="flex items-center gap-2 mt-8">
            {featuredComics.map((_, idx) => (
              <button
                key={idx}
                onClick={() => setActiveIndex(idx)}
                className={`w-2.5 h-2.5 rounded-full transition-all ${idx === activeIndex ? 'bg-white w-8' : 'bg-white/40 hover:bg-white/60'}`}
                aria-label={`第${idx + 1}张`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function CategoryNav() {
  return (
    <section className="max-w-7xl mx-auto px-4 md:px-6 -mt-10 relative z-30">
      <div className="bg-white rounded-xl p-4 md:p-6 shadow-sm">
        <div className="flex items-center gap-3 flex-wrap">
          {categories.map((cat) => (
            <Link
              key={cat.slug}
              to={cat.slug === 'all' ? '/category/all' : `/category/${cat.slug}`}
              className="px-4 py-2 text-sm font-medium rounded-full bg-background-100 text-foreground-700 hover:bg-primary-500 hover:text-white transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              {cat.name}
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function HotSection() {
  return (
    <section className="max-w-7xl mx-auto px-4 md:px-6 mt-16">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl md:text-2xl font-bold text-foreground-900">
          <i className="ri-fire-line text-primary-500 mr-2"></i>
          热门漫画
        </h2>
        <Link to="/category/all?sort=hot" className="text-sm text-foreground-500 hover:text-primary-600 transition-colors flex items-center gap-1 whitespace-nowrap">
          查看更多 <i className="ri-arrow-right-s-line"></i>
        </Link>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
        {hotComics.map((comic: Comic) => (
          <ComicCard key={comic.id} comic={comic} />
        ))}
      </div>
    </section>
  );
}

function LatestSection() {
  return (
    <section className="max-w-7xl mx-auto px-4 md:px-6 mt-16">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl md:text-2xl font-bold text-foreground-900">
          <i className="ri-time-line text-secondary-500 mr-2"></i>
          最近更新
        </h2>
        <Link to="/category/all?sort=latest" className="text-sm text-foreground-500 hover:text-primary-600 transition-colors flex items-center gap-1 whitespace-nowrap">
          查看更多 <i className="ri-arrow-right-s-line"></i>
        </Link>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 md:gap-5">
        {latestComics.map((comic: Comic) => (
          <ComicCard key={comic.id} comic={comic} size="small" />
        ))}
      </div>
    </section>
  );
}

function RankingSection() {
  return (
    <section className="max-w-7xl mx-auto px-4 md:px-6 mt-16 mb-16">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl md:text-2xl font-bold text-foreground-900">
          <i className="ri-trophy-line text-accent-500 mr-2"></i>
          人气排行榜
        </h2>
      </div>
      <div className="bg-white rounded-xl p-4 md:p-6">
        <div className="space-y-0">
          {rankingComics.map((comic: Comic, idx: number) => (
            <Link
              key={comic.id}
              to={`/article/${comic.id}`}
              className="flex items-center gap-4 py-3 px-2 rounded-lg hover:bg-background-50 transition-colors group cursor-pointer"
            >
              <span className={`w-8 h-8 flex items-center justify-center rounded-full text-sm font-bold ${idx < 3 ? 'bg-primary-500 text-white' : 'bg-background-100 text-foreground-500'}`}>
                {idx + 1}
              </span>
              <div className="w-10 h-14 rounded overflow-hidden flex-shrink-0 bg-background-100">
                <img src={comic.cover} alt={comic.title} className="w-full h-full object-cover object-top" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-1">
                  {comic.title}
                </h3>
                <p className="text-xs text-foreground-500 mt-0.5">{comic.author} · {comic.category}</p>
              </div>
              <div className="text-right flex-shrink-0">
                <div className="flex items-center gap-1 text-amber-500 text-xs">
                  <i className="ri-star-fill"></i>
                  <span className="font-semibold">{comic.rating}</span>
                </div>
                <p className="text-xs text-foreground-400 mt-0.5">
                  {comic.views > 10000 ? `${(comic.views / 10000).toFixed(1)}万` : comic.views} 阅读
                </p>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function FeaturesSection() {
  const features = [
    { icon: 'ri-book-open-line', title: '海量资源', desc: '收录数千部优质韩国漫画，品类齐全，应有尽有' },
    { icon: 'ri-flashlight-line', title: '每日更新', desc: '热门漫画每日同步更新，追更不等待，精彩不错过' },
    { icon: 'ri-smartphone-line', title: '多端畅读', desc: '支持电脑、平板、手机多设备阅读，随时随地畅享漫画' },
    { icon: 'ri-shield-check-line', title: '正版授权', desc: '与韩国多家漫画平台合作，提供正版优质阅读体验' },
  ];

  return (
    <section className="bg-background-100 mt-16 py-16">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="text-center mb-10">
          <h2 className="text-xl md:text-2xl font-bold text-foreground-900">为什么选择哈哈漫画？</h2>
          <p className="text-sm text-foreground-500 mt-2">为您提供最优质的韩国漫画阅读体验</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {features.map((f, idx) => (
            <div key={idx} className="bg-white rounded-xl p-6 text-center hover:shadow-sm transition-shadow">
              <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-primary-100 flex items-center justify-center">
                <i className={`${f.icon} text-xl text-primary-600`}></i>
              </div>
              <h3 className="text-sm font-semibold text-foreground-900 mb-2">{f.title}</h3>
              <p className="text-xs text-foreground-500 leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <div className="min-h-screen bg-background-50">
      <HeroBanner />
      <CategoryNav />
      <HotSection />
      <LatestSection />
      <FeaturesSection />
      <RankingSection />
    </div>
  );
}